/**
 * StatsSection — Morgan Legal Group
 * Design: Dark navy band with animated gold numbers
 * Scroll-triggered counter animation
 */
import { useEffect, useRef, useState } from "react";

type Stat = {
  value: number;
  suffix: string;
  label: string;
  description: string;
  plain?: boolean;
};

const stats: Stat[] = [
  { value: 100, suffix: "+", label: "Years of Combined Experience", description: "Across our entire legal team" },
  { value: 5000, suffix: "+", label: "Clients Served", description: "Across New York State" },
  { value: 900, suffix: "+", label: "5-Star Reviews", description: "Across all platforms" },
  // `plain` — год, а не количество: `toLocaleString()` напечатал бы «2,017».
  { value: 2017, suffix: "", label: "Established", description: "Serving clients since 2017", plain: true },
];

function useCountUp(target: number, duration: number = 1800, start: boolean = false) {
  // Начальное значение — КОНЕЧНОЕ, а не 0. Полоса собирается пререндером, и с
  // нулём в разметке в статическом HTML стояло `0+` — то, что видят краулеры и
  // ИИ-поверхности, и то, что осталось бы на экране, не сработай наблюдатель.
  // Отсчёт всё равно начинается с нуля: первый кадр анимации сам ставит 0.
  const [count, setCount] = useState(target);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function StatItem({ stat, animate }: { stat: Stat; animate: boolean }) {
  const count = useCountUp(stat.value, 1800, animate);
  return (
    <div className="text-center lg:text-left border-b lg:border-b-0 lg:border-r border-white/10 last:border-0 py-8 lg:py-0 lg:px-8 first:lg:pl-0 last:lg:pr-0">
      <div className="stat-number">
        {stat.plain ? count : count.toLocaleString()}{stat.suffix}
      </div>
      <div className="font-display text-white text-base lg:text-lg font-semibold mt-2 mb-1">
        {stat.label}
      </div>
      <div className="font-body text-white/50 text-sm">
        {stat.description}
      </div>
    </div>
  );
}

export default function StatsSection() {
  const [animate, setAnimate] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setAnimate(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="stats" ref={ref} className="bg-navy py-12 lg:py-16 relative overflow-hidden">
      {/* Decorative gold line */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-0">
          {stats.map((stat) => (
            <StatItem key={stat.label} stat={stat} animate={animate} />
          ))}
        </div>
      </div>
    </section>
  );
}
